import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../../components/Layout";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Shield, EyeOff, FileCheck2, Sparkles, Lock, Search } from "lucide-react";
import { toast } from "sonner";
import {
  SECTORS, FUND_MODELS, BACKGROUNDS,
  TICKET_BUCKETS, REVENUE_BUCKETS,
} from "../../lib/taxonomy";
import GeographySelect from "../../components/GeographySelect";
import BucketRangeSlider from "../../components/BucketRangeSlider";
import { t } from "../../lib/i18n";

const indexOfNearest = (buckets, value) => {
  if (value == null) return null;
  let best = 0;
  let bestDist = Math.abs(buckets[0] - value);
  for (let i = 1; i < buckets.length; i++) {
    const d = Math.abs(buckets[i] - value);
    if (d < bestDist) { best = i; bestDist = d; }
  }
  return best;
};

export default function InvestorOnboarding() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const existing = user?.investor_profile || {};
  const isEdit = Boolean(user?.investor_terms_accepted);

  // Migración suave del perfil viejo → nuevos rangos.
  const initialTicket = useMemo(() => {
    const minI = indexOfNearest(TICKET_BUCKETS, existing.ticket_min_eur);
    const maxI = indexOfNearest(TICKET_BUCKETS, existing.ticket_max_eur ?? existing.ticket_size_eur);
    return [minI ?? 0, maxI ?? TICKET_BUCKETS.length - 1];
  }, [existing.ticket_min_eur, existing.ticket_max_eur, existing.ticket_size_eur]);

  const initialRevenue = useMemo(() => {
    const minI = indexOfNearest(REVENUE_BUCKETS, existing.revenue_min_eur);
    const maxI = indexOfNearest(REVENUE_BUCKETS, existing.revenue_max_eur);
    return [minI ?? 0, maxI ?? REVENUE_BUCKETS.length - 1];
  }, [existing.revenue_min_eur, existing.revenue_max_eur]);

  const initialGeos = useMemo(() => {
    if (Array.isArray(existing.geographies) && existing.geographies.length) return existing.geographies;
    return [];
  }, [existing.geographies]);

  const [form, setForm] = useState({
    investment_type: existing.investment_type || "",
    ticket: initialTicket,
    revenue: initialRevenue,
    geographies: initialGeos,
    sectors_interest: existing.sectors_interest || [],
    gross_margin_min_pct: existing.gross_margin_min_pct ?? "",
    ebitda_margin_min_pct: existing.ebitda_margin_min_pct ?? "",
    min_searcher_experience: existing.min_searcher_experience ?? "",
    fund_models_backed: existing.fund_models_backed || [],
    required_dedication: existing.required_dedication || "",
    preferred_backgrounds: existing.preferred_backgrounds || [],
    organization: existing.organization || "",
    linkedin_url: existing.linkedin_url || "",
    website_url: existing.website_url || "",
    bio: existing.bio || "",
  });
  const [loading, setLoading] = useState(false);

  const toggleIn = (key, v) => {
    setForm((prev) => ({
      ...prev,
      [key]: prev[key].includes(v) ? prev[key].filter((x) => x !== v) : [...prev[key], v],
    }));
  };
  const toggleSector = (s) => toggleIn("sectors_interest", s);

  const submit = async () => {
    if (!form.investment_type) {
      toast.error(t("Indica el tipo de inversión."));
      return;
    }
    const [tMinIdx, tMaxIdx] = form.ticket;
    const [rMinIdx, rMaxIdx] = form.revenue;
    setLoading(true);
    try {
      await api.post("/users/investor-profile", {
        investment_type: form.investment_type,
        ticket_min_eur: TICKET_BUCKETS[tMinIdx],
        ticket_max_eur: tMaxIdx === TICKET_BUCKETS.length - 1 ? null : TICKET_BUCKETS[tMaxIdx],
        sectors_interest: form.sectors_interest,
        geographies: form.geographies,
        revenue_min_eur: REVENUE_BUCKETS[rMinIdx],
        revenue_max_eur: rMaxIdx === REVENUE_BUCKETS.length - 1 ? null : REVENUE_BUCKETS[rMaxIdx],
        gross_margin_min_pct: form.gross_margin_min_pct !== "" ? Number(form.gross_margin_min_pct) : null,
        ebitda_margin_min_pct: form.ebitda_margin_min_pct !== "" ? Number(form.ebitda_margin_min_pct) : null,
        min_searcher_experience: form.min_searcher_experience !== "" ? Number(form.min_searcher_experience) : null,
        fund_models_backed: form.fund_models_backed,
        required_dedication: form.required_dedication || null,
        preferred_backgrounds: form.preferred_backgrounds,
        organization: form.organization.trim() || null,
        linkedin_url: form.linkedin_url.trim() || null,
        website_url: form.website_url.trim() || null,
        bio: form.bio.trim() || null,
        accept_terms: true,
      });
      await refresh();
      toast.success(isEdit ? "Perfil actualizado" : "Perfil completado");
      navigate("/investor/marketplace");
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Error guardando el perfil");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-6 lg:px-10 py-12 lg:py-16">
        <span className="font-mono-label text-gold">{t("Onboarding · Inversor")}</span>
        <h1 className="font-serif-display text-4xl md:text-5xl lg:text-6xl mt-3 mb-4 leading-tight">
          {t("Listo,")}{" "}{user?.name?.split(" ")[0]}{t(". Vamos a personalizar tu acceso.")}
        </h1>
        <p className="text-base md:text-lg text-muted-foreground max-w-2xl">
          {t("Cuéntanos qué buscas. Tus criterios alimentan el Match IA: solo verás operaciones que cumplan tus filtros.")}
        </p>

        {/* Confidentiality pillars */}
        <div className="mt-12 grid md:grid-cols-2 gap-0 hairline border-r-0 border-b-0">
          <Pillar icon={<Shield className="w-5 h-5" />} title={t("Confidencialidad mutua")}
                  text={t("La información detallada solo se abre cuando tú expresas interés y el vendedor lo aprueba. Comprométete a usarla únicamente para evaluar la oportunidad.")} />
          <Pillar icon={<EyeOff className="w-5 h-5" />} title={t("Teaser ciego primero")}
                  text={t("Ves nombre código, sector, datos clave y términos del deal. Identidad real, equipo y due diligence solo bajo NDA específico.")} />
          <Pillar icon={<FileCheck2 className="w-5 h-5" />} title={t("Tú decides cada paso")}
                  text={t("Expresar interés no compromete capital. Es la primera señal para que searcher y vendedor te incluyan en el proceso.")} />
          <Pillar icon={<Sparkles className="w-5 h-5" />} title={t("Tus criterios filtran")}
                  text={t("Tipo, ticket, sector, geografía, facturación y márgenes guían qué oportunidades se te muestran y notifican.")} />
        </div>

        {/* Profile form */}
        <div className="mt-12 hairline bg-card">
          <div className="px-7 py-5 hairline-b bg-bone">
            <span className="font-mono-label text-gold">{t("Tu perfil de inversión")}</span>
            <h2 className="font-serif-display text-2xl mt-1">{t("Define tus criterios")}</h2>
          </div>
          <div className="p-7 space-y-8">
            <div className="grid md:grid-cols-2 gap-x-8 gap-y-6">
              <Field label={t("Tipo de inversión *")}>
                <Select value={form.investment_type} onValueChange={(v) => setForm({ ...form, investment_type: v })}>
                  <SelectTrigger className="rounded-none h-11" data-testid="investor-type-select">
                    <SelectValue placeholder={t("Selecciona…")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="equity">{t("Equity")}</SelectItem>
                    <SelectItem value="debt">{t("Deuda")}</SelectItem>
                    <SelectItem value="mixed">{t("Mixto")}</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </div>

            <BucketRangeSlider
              label={t("Rango de ticket (€)")}
              buckets={TICKET_BUCKETS}
              value={form.ticket}
              onChange={(v) => setForm({ ...form, ticket: v })}
              testPrefix="investor-ticket"
            />

            <BucketRangeSlider
              label={t("Rango de facturación de la empresa objetivo (€)")}
              buckets={REVENUE_BUCKETS}
              value={form.revenue}
              onChange={(v) => setForm({ ...form, revenue: v })}
              testPrefix="investor-revenue"
            />

            <div className="grid md:grid-cols-2 gap-x-8 gap-y-6">
              <Field label={t("Margen bruto mínimo exigido (%)")}>
                <Input
                  type="number" min="0" max="100"
                  value={form.gross_margin_min_pct}
                  onChange={(e) => setForm({ ...form, gross_margin_min_pct: e.target.value })}
                  placeholder={t("vacío = sin requisito (p. ej. 40)")}
                  className="rounded-none h-11"
                  data-testid="investor-gross-margin-min-input"
                />
              </Field>
              <Field label={t("Margen EBITDA mínimo exigido (%)")}>
                <Input
                  type="number" min="0" max="100"
                  value={form.ebitda_margin_min_pct}
                  onChange={(e) => setForm({ ...form, ebitda_margin_min_pct: e.target.value })}
                  placeholder={t("vacío = sin requisito (p. ej. 15)")}
                  className="rounded-none h-11"
                  data-testid="investor-ebitda-margin-min-input"
                />
              </Field>
            </div>

            <GeographySelect
              label={t("Geografías de interés (España toda + CCAA y/o Resto del mundo)")}
              value={form.geographies}
              onChange={(v) => setForm({ ...form, geographies: v })}
              testPrefix="investor-geo"
            />

            <div className="space-y-2">
              <Label className="font-mono-label text-muted-foreground">{t("Sectores de interés")}</Label>
              <div className="flex flex-wrap gap-2">
                {SECTORS.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => toggleSector(s)}
                    data-testid={`sector-chip-${s}`}
                    className={`hairline px-3 py-1.5 text-sm transition-colors ${
                      form.sectors_interest.includes(s)
                        ? "bg-[#1E3A5F] text-bone border-[#1E3A5F]"
                        : "bg-card hover:bg-bone"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Searcher requirements — closes the match triangle */}
        <div className="mt-8 hairline bg-card" data-testid="investor-searcher-reqs-section">
          <div className="px-7 py-5 hairline-b bg-bone flex items-center gap-3">
            <div className="w-9 h-9 hairline flex items-center justify-center bg-[#1E3A5F]">
              <Search className="w-4 h-4 text-gold" />
            </div>
            <div>
              <span className="font-mono-label text-gold">{t("Cierra el triángulo")}</span>
              <h2 className="font-serif-display text-2xl mt-0.5">{t("Qué exiges a un Searcher")}</h2>
            </div>
          </div>
          <div className="p-7 space-y-7">
            <p className="text-sm text-muted-foreground -mt-1">
              {t("Los searchers responden exactamente sobre estas dimensiones en su perfil. Tus requisitos alimentan el Match IA para presentarte solo operaciones lideradas por el perfil que respaldarías.")}
            </p>
            <div className="grid md:grid-cols-2 gap-x-8 gap-y-6">
              <Field label={t("Experiencia mínima exigida (años)")}>
                <Input
                  type="number" min="0"
                  value={form.min_searcher_experience}
                  onChange={(e) => setForm({ ...form, min_searcher_experience: e.target.value })}
                  placeholder={t("p. ej. 5 (vacío = sin requisito)")}
                  className="rounded-none h-11"
                  data-testid="investor-minexp-input"
                />
              </Field>
              <Field label={t("Dedicación exigida")}>
                <div className="flex flex-wrap gap-2">
                  {[{ v: "full_time", l: t("Full-time exigido") }, { v: "indiferente", l: "Indiferente" }].map((o) => (
                    <button key={o.v} type="button"
                            onClick={() => setForm({ ...form, required_dedication: form.required_dedication === o.v ? "" : o.v })}
                            data-testid={`investor-dedication-${o.v}`}
                            className={`hairline px-3 py-1.5 text-sm transition-colors ${form.required_dedication === o.v ? "bg-[#1E3A5F] text-bone border-[#1E3A5F]" : "bg-card hover:bg-bone"}`}>
                      {o.l}
                    </button>
                  ))}
                </div>
              </Field>
            </div>
            <div className="space-y-2">
              <Label className="font-mono-label text-muted-foreground">{t("Modelos de search fund que respaldas (vacío = todos)")}</Label>
              <div className="grid md:grid-cols-4 gap-0 hairline border-r-0 border-b-0">
                {FUND_MODELS.map((m) => (
                  <button key={m.value} type="button" onClick={() => toggleIn("fund_models_backed", m.value)}
                          data-testid={`investor-fundmodel-${m.value}`}
                          className={`text-left hairline border-l-0 border-t-0 p-4 transition-colors ${form.fund_models_backed.includes(m.value) ? "bg-[#1E3A5F] text-bone" : "bg-card hover:bg-bone"}`}>
                    <div className="font-serif-display text-lg mb-1">{m.label}</div>
                    <div className={`text-xs ${form.fund_models_backed.includes(m.value) ? "text-bone/70" : "text-muted-foreground"}`}>{m.desc}</div>
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label className="font-mono-label text-muted-foreground">{t("Backgrounds que más valoras (opcional)")}</Label>
              <div className="flex flex-wrap gap-2">
                {BACKGROUNDS.map((b) => (
                  <button key={b} type="button" onClick={() => toggleIn("preferred_backgrounds", b)}
                          data-testid={`investor-background-${b}`}
                          className={`hairline px-3 py-1.5 text-sm transition-colors ${form.preferred_backgrounds.includes(b) ? "bg-[#1E3A5F] text-bone border-[#1E3A5F]" : "bg-card hover:bg-bone"}`}>
                    {b}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Presentación al vendedor */}
        <div className="mt-8 hairline bg-card" data-testid="investor-identity-card">
          <div className="px-6 py-4 hairline-b bg-bone">
            <span className="font-mono-label text-gold">{t("Tu presentación")}</span>
            <p className="text-xs text-muted-foreground mt-0.5">
              {t("El vendedor verá estos datos al decidir si aprueba tu NDA. Cuanto más claro, más rápido te darán acceso al IM.")}
            </p>
          </div>
          <div className="p-6 grid md:grid-cols-2 gap-5">
            <div>
              <Label className="font-mono-label text-muted-foreground" style={{ fontSize: "0.6rem" }}>{t("Organización / vehículo inversor")}</Label>
              <Input value={form.organization} onChange={(e) => setForm({ ...form, organization: e.target.value })}
                     placeholder={t("p. ej. Family office Martínez · Business angel")} className="rounded-none mt-1.5" data-testid="investor-organization" />
            </div>
            <div>
              <Label className="font-mono-label text-muted-foreground" style={{ fontSize: "0.6rem" }}>{t("Perfil de LinkedIn")}</Label>
              <Input value={form.linkedin_url} onChange={(e) => setForm({ ...form, linkedin_url: e.target.value })}
                     placeholder={t("https://linkedin.com/in/…")} className="rounded-none mt-1.5" data-testid="investor-linkedin" />
            </div>
            <div>
              <Label className="font-mono-label text-muted-foreground" style={{ fontSize: "0.6rem" }}>{t("Web")}</Label>
              <Input value={form.website_url} onChange={(e) => setForm({ ...form, website_url: e.target.value })}
                     placeholder={t("https://")} className="rounded-none mt-1.5" data-testid="investor-website" />
            </div>
            <div className="md:col-span-2">
              <Label className="font-mono-label text-muted-foreground" style={{ fontSize: "0.6rem" }}>{t("Breve presentación")}</Label>
              <Textarea value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} rows={3} maxLength={800}
                        placeholder={t("Quién eres, tu experiencia invirtiendo y qué buscas en una operación de search fund…")}
                        className="rounded-none mt-1.5 text-sm" data-testid="investor-bio" />
            </div>
          </div>
        </div>

        {/* Nota de confidencialidad */}
        <div className="mt-8 hairline p-6 bg-card flex items-start gap-3" data-testid="investor-confidentiality-note">
          <Shield className="w-4 h-4 text-gold mt-0.5 flex-shrink-0" />
          <p className="text-sm text-muted-foreground leading-relaxed">
            {t("Al registrarte, te unes a una red privada de inversión donde la confidencialidad es la norma. Los acuerdos específicos se gestionarán individualmente para cada oportunidad.")}
          </p>
        </div>

        <div className="mt-8 flex flex-wrap items-center justify-between gap-4">
          <span className="font-mono-label text-muted-foreground flex items-center gap-2">
            <Lock className="w-3 h-3" />{" "}{t("Almacenamiento cifrado · HTTPS · Datos en UE")}
          </span>
          <Button
            onClick={submit}
            disabled={loading}
            className="rounded-none bg-[#1E3A5F] hover:bg-[#2B4C78] text-bone h-12 px-8 disabled:opacity-50"
            data-testid="investor-submit-btn"
          >
            {loading ? t("Procesando…") : isEdit ? t("Guardar cambios") : t("Entrar al marketplace")}
          </Button>
        </div>
      </div>
    </Layout>
  );
}

const Pillar = ({ icon, title, text }) => (
  <div className="hairline border-l-0 border-t-0 p-7 bg-card">
    <div className="w-10 h-10 hairline flex items-center justify-center mb-5 bg-bone">{icon}</div>
    <h3 className="font-serif-display text-xl mb-2">{title}</h3>
    <p className="text-sm text-muted-foreground leading-relaxed">{text}</p>
  </div>
);

const Field = ({ label, children }) => (
  <div className="space-y-2">
    <Label className="font-mono-label text-muted-foreground">{label}</Label>
    {children}
  </div>
);
